import {
  type QueryFunctionContext,
  useInfiniteQuery,
} from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import * as R from 'remeda'

import { auth0UserAtom } from '~/atoms'
import { graphql } from '~/gql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { addProp, equalsByPath } from '~/utils'

import { useArchivedStrategyMemosInfiniteQueryKey } from './useArchivedStrategyMemosInfiniteQueryKey'

const document = graphql(`
  query StrategyMemoArchiveListPage_StrategyMemos(
    $brandAccountId: ID!
    $offset: Int! = 0
  ) {
    customerStrategyMemos(
      brandAccountId: $brandAccountId
      status: archived
      pagination: { limit: 20, offset: $offset }
    ) {
      id
      title
      writer
    }
  }
`)

type QueryKey = ReturnType<typeof useArchivedStrategyMemosInfiniteQueryKey>
type PageParam = number

export const useArchivedStrategyMemosInfiniteQuery = () => {
  const queryKey = useArchivedStrategyMemosInfiniteQueryKey()
  const graphQLRequest = useGraphQLRequest()
  const auth0User = useAtomValue(auth0UserAtom)

  return useInfiniteQuery({
    queryFn: async ({
      pageParam: offset,
      queryKey: [_, variables],
    }: QueryFunctionContext<QueryKey, PageParam>) => {
      const result = await graphQLRequest({
        document,
        variables: { ...variables, offset },
      })

      return R.pipe(
        result.customerStrategyMemos ?? [],
        R.map(
          R.piped(
            addProp(
              'isDeletable',
              equalsByPath('writer', auth0User?.email ?? ''),
            ),
          ),
        ),
      )
    },
    queryKey,
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages): PageParam | undefined =>
      0 < lastPage.length ? allPages.flat().length : undefined,
  })
}

export type StrategyMemo = QueryResultOf<
  typeof useArchivedStrategyMemosInfiniteQuery
>[number]
